// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { OffsetOptions, ShiftOptions } from "@floating-ui/dom";
import { UseHoverProps } from "@floating-ui/react/dist/floating-ui.react";

import { TooltipContext } from "@awc/App/Components/FloatingTooltip/TooltipContext";
import { xHeaderUpdate } from "@awc/App/Handlers/XNG/Header";
import { FocusableSelectors, useTooltip } from "@awc/App/Hooks/useTooltip";
import Button, { ButtonIcon, ButtonPresentationProps } from "@awc/MobiusOverrides/Button/Button";
import { addScrollbarGutter, mergeWithCss, removeScrollbarGutter } from "@awc/XDL/Utilities";
import {
    FloatingArrow,
    FloatingArrowProps,
    FloatingNode,
    FloatingPortal,
    FloatingTree,
    Placement,
    useFloatingParentNodeId,
    useTransitionStyles,
} from "@floating-ui/react";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import X from "@insite/mobius/Icons/X";
import * as ariaAppHider from "@insite/mobius/Overlay/helpers/ariaAppHider";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, FocusEvent, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";

export type FloatingTooltipProps = {
    isOpen?: boolean;
    placement?: Placement;
    trigger?: "hover" | "click" | "manual";
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    offset?: OffsetOptions;
    shift?: ShiftOptions;
    delay?: UseHoverProps["delay"];
    showArrow?: boolean;
    overlay?: boolean;
    triggerComponent: React.ReactNode;
    enabled?: boolean;
    drawer?: boolean;
    extendedStyles?: FloatingTooltipStyles;
};

interface Props extends FloatingTooltipProps {
    children: React.ReactNode;
}

export interface FloatingTooltipStyles {
    tooltipContent?: InjectableCss;
    tooltipArrow?: Omit<FloatingArrowProps, "context">;
    drawerContent?: InjectableCss;
    drawerClose?: ButtonPresentationProps;
}

export const floatingTooltipStyles: FloatingTooltipStyles = {};

const FloatingTooltipComponent: FC<Props> = ({
    children,
    enabled = true,
    triggerComponent,
    overlay = true,
    extendedStyles,
    showArrow = true,
    drawer = false,
    trigger = "hover",
    ...options
}) => {
    const location = useAppSelector(o => getLocation(o));
    const arrowRef = useRef<SVGSVGElement | null>(null);
    const tooltip = useTooltip({
        ...options,
        trigger,
        showArrow,
        arrowRef: arrowRef as React.RefObject<SVGSVGElement>,
    });
    const { isMounted, styles: translationStyles } = useTransitionStyles(tooltip.context);

    const [styles] = useState(() => mergeWithCss(floatingTooltipStyles, extendedStyles));
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (overlay) {
            dispatch(
                xHeaderUpdate({
                    key: isMounted ? "registerMenu" : "unregisterMenu",
                    value: `${tooltip.nodeId}_tooltip`,
                }),
            );
            isMounted ? addScrollbarGutter() : removeScrollbarGutter();
            isMounted ? ariaAppHider.hide(document.body) : ariaAppHider.show(document.body);

            if (drawer) {
                isMounted ? document.body.classList.add("drawer-open") : document.body.classList.remove("drawer-open");
            }
        }
    }, [isMounted]);

    useEffect(() => {
        tooltip.setOpen(false);
        options.onOpenChange?.(false);
    }, [location.pathname]);

    if (!enabled) {
        return <>{triggerComponent}</>;
    }

    return (
        <FloatingNode id={tooltip.nodeId}>
            <TooltipContext.Provider value={{ ...tooltip, open: isMounted }}>
                <span
                    ref={tooltip.refs.setReference}
                    {...tooltip.getReferenceProps()}
                    data-state={isMounted ? "open" : "closed"}
                >
                    {triggerComponent}
                </span>
                <FloatingPortal>
                    <StyledWrapper
                        ref={tooltip.refs.setFloating}
                        style={
                            !drawer
                                ? {
                                      ...tooltip.floatingStyles,
                                      ...translationStyles,
                                      display: isMounted ? "block" : "none",
                                  }
                                : {}
                        }
                        {...(drawer ? styles.drawerContent : styles.tooltipContent)}
                        {...{ isMounted }}
                        {...tooltip.getFloatingProps({
                            onFocus(event: FocusEvent<HTMLElement>) {
                                const target = event.target as HTMLElement;
                                if (
                                    (trigger === "hover" && target.matches(FocusableSelectors)) ||
                                    target.isContentEditable
                                ) {
                                    tooltip.setHoverEnabled(false);
                                }
                            },
                            onBlur(event: FocusEvent<HTMLElement>) {
                                const target = event.target as HTMLElement;
                                if (
                                    (trigger === "hover" && target.matches(FocusableSelectors)) ||
                                    target.isContentEditable
                                ) {
                                    tooltip.setHoverEnabled(true);
                                }
                            },
                        })}
                    >
                        {drawer && (
                            <Button {...styles.drawerClose} onClick={() => tooltip.setOpen(false)}>
                                <ButtonIcon src={X} size={15} />
                            </Button>
                        )}
                        {children}
                        {!drawer && showArrow && (
                            <FloatingArrow ref={arrowRef} context={tooltip.context} {...styles.tooltipArrow} />
                        )}
                    </StyledWrapper>
                </FloatingPortal>
            </TooltipContext.Provider>
        </FloatingNode>
    );
};

export function FloatingTooltip(props: Props) {
    const parentId = useFloatingParentNodeId();
    if (parentId === null) {
        return (
            <FloatingTree>
                <FloatingTooltipComponent {...props} />
            </FloatingTree>
        );
    }
    return <FloatingTooltipComponent {...props} />;
}

export default FloatingTooltip;
